import { Check, ChevronsUpDown, Building2 } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";

interface OrgBrand {
  name: string;
  logo_url: string | null;
  brand_primary: string | null;
}

function OrgAvatar({ name, logoUrl, brandColor, size = 36 }: { name: string; logoUrl: string | null; brandColor?: string | null; size?: number }) {
  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="object-contain shrink-0"
        style={{ height: size, maxWidth: size * 3.3 }}
      />
    );
  }
  return (
    <div
      className="rounded-full flex items-center justify-center text-white font-bold shrink-0"
      style={{ width: size, height: size, fontSize: size * 0.4, backgroundColor: brandColor ?? "#0D2240" }}
    >
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

/**
 * Lets a user with active membership in more than one institution switch the
 * dashboard's active context. Renders nothing for single-institution users so
 * it adds no UI noise for the common case.
 */
export function OrgSwitcher({ orgBrand }: { orgBrand?: OrgBrand | null }) {
  const { orgId, availableOrgs, hasMultipleOrgs, switchOrg, loading } = useOrgPermissions();

  if (!orgBrand) return null;

  if (!hasMultipleOrgs) {
    return (
      <div className="flex flex-col items-center gap-1.5 py-2 border border-sidebar-border/40 rounded-lg bg-sidebar-accent/20">
        <OrgAvatar name={orgBrand.name} logoUrl={orgBrand.logo_url} brandColor={orgBrand.brand_primary} />
        <span className="text-[11px] font-semibold text-sidebar-foreground text-center leading-tight px-2 line-clamp-2">
          {orgBrand.name}
        </span>
        <span className="text-[9px] uppercase tracking-widest text-muted-foreground">Dashboard</span>
      </div>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          disabled={loading}
          className="w-full flex flex-col items-center gap-1.5 py-2 border border-sidebar-border/40 rounded-lg bg-sidebar-accent/20 hover:bg-sidebar-accent/40 transition-colors disabled:opacity-60"
        >
          <OrgAvatar name={orgBrand.name} logoUrl={orgBrand.logo_url} brandColor={orgBrand.brand_primary} />
          <span className="flex items-center gap-1 text-[11px] font-semibold text-sidebar-foreground text-center leading-tight px-2">
            <span className="line-clamp-1">{orgBrand.name}</span>
            <ChevronsUpDown className="w-3 h-3 shrink-0 text-muted-foreground" />
          </span>
          <span className="text-[9px] uppercase tracking-widest text-muted-foreground">
            {availableOrgs.length} institutions
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
          <Building2 className="w-3.5 h-3.5" /> Switch institution
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {availableOrgs.map((org) => (
          <DropdownMenuItem
            key={org.orgId}
            onClick={() => switchOrg(org.orgId)}
            className="flex items-center gap-2.5 py-2 cursor-pointer"
          >
            <OrgAvatar name={org.orgName} logoUrl={org.logoUrl} size={24} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{org.orgName}</div>
              {org.roleName && <div className="text-[11px] text-muted-foreground truncate">{org.roleName}</div>}
            </div>
            {org.orgId === orgId && <Check className="w-4 h-4 text-primary shrink-0" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
